// frontend/src/pages/ChartOfAccounts/AccountFormModal.jsx
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { coaApi } from "./coaApi";

const emptyForm = {
  main_type: "",
  accounting_type: "",
  parent_account: "",
  account_code: "",
  account_name: "",
  company_id: "",
  description: "",
  currency_code: "USD", // fallback; overridden per company on open
  opening_side: "none",
  opening_amount: "",
};

const MAIN_TYPE_ORDER = [
  "Asset",
  "Liability",
  "Equity",
  "Income",
  "Expense",
];

const MAIN_TYPE_LABELS = {
  Asset: "Assets",
  Liability: "Liabilities",
  Equity: "Equity",
  Income: "Income",
  Expense: "Cost of Sales & Expenses",
};

const canonicalMainType = (value) => {
  if (value === "Capital") return "Equity";
  if (value === "Revenue") return "Income";
  return value || "";
};

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id || value.id || "";
  return value;
};

const unwrapArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.accounts)) return payload.accounts;
  if (Array.isArray(payload?.companies)) return payload.companies;
  if (Array.isArray(payload?.accountingTypes)) return payload.accountingTypes;
  return [];
};

const getAccountType = (account) => {
  if (account?.accountingType) return account.accountingType;
  if (
    account?.accounting_type &&
    typeof account.accounting_type === "object"
  ) {
    return account.accounting_type;
  }
  return {};
};

// ⭐ Sanitize a currency code to a 3-letter uppercase value
const normalizeCurrencyCode = (value, fallback = "USD") => {
  const code = String(value || "").trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(code)) return code;
  return fallback;
};

export default function AccountFormModal({
  open,
  account,
  accountTree,
  defaultCompanyId = "",
  defaultCurrencyCode = "USD", // ⭐ NEW
  onClose,
  onSaved,
}) {
  const { currentUser, userType, company } = useAuth();

  const isSystemAdmin =
    userType === "system" &&
    ["developer", "admin"].includes(currentUser?.role);

  const contextCompanyId =
    userType === "company"
      ? getId(company) || getId(currentUser?.company_id)
      : "";

  // ⭐ Resolve a safe default currency for this modal instance
  const resolvedDefaultCurrency = useMemo(
    () => normalizeCurrencyCode(defaultCurrencyCode, "USD"),
    [defaultCurrencyCode],
  );

  const [form, setForm] = useState(emptyForm);
  const [companies, setCompanies] = useState([]);
  const [accountingTypes, setAccountingTypes] = useState([]);
  const [parentAccounts, setParentAccounts] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingReferences, setLoadingReferences] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    const companyId =
      getId(account?.company_id) || getId(defaultCompanyId) || contextCompanyId;

    if (!account) {
      setForm({
        ...emptyForm,
        company_id: companyId,
        // ⭐ Default new accounts to the company's currency
        currency_code: resolvedDefaultCurrency,
      });
      setError("");
      return;
    }

    const typeDetails = getAccountType(account);
    const openingDebit = Number(account.opening_debit || 0);
    const openingCredit = Number(account.opening_credit || 0);

    setForm({
      main_type: canonicalMainType(typeDetails.type || account.type),
      accounting_type:
        getId(account.accounting_type) || getId(account.accountingType),
      parent_account:
        getId(account.parent_account) || getId(account.parentAccount),
      account_code: account.account_code || "",
      account_name: account.account_name || "",
      company_id: companyId,
      description: account.description || "",
      // ⭐ Prefer the account's own currency, then company default, then USD
      currency_code: normalizeCurrencyCode(
        account.currency_code ||
          account.currencyDetails?.currency_code ||
          resolvedDefaultCurrency,
        resolvedDefaultCurrency,
      ),
      opening_side:
        openingDebit > 0
          ? "debit"
          : openingCredit > 0
            ? "credit"
            : "none",
      opening_amount:
        openingDebit > 0
          ? String(openingDebit)
          : openingCredit > 0
            ? String(openingCredit)
            : "",
    });
    setError("");
  }, [
    open,
    account,
    contextCompanyId,
    defaultCompanyId,
    resolvedDefaultCurrency,
  ]);

  useEffect(() => {
    if (!open || !isSystemAdmin) return;

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const response = await coaApi.getCompanies();
        setCompanies(unwrapArray(response));
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, [open, isSystemAdmin]);

  useEffect(() => {
    if (!open) return;

    const companyId = getId(form.company_id);
    if (isSystemAdmin && !companyId) {
      setAccountingTypes([]);
      setParentAccounts([]);
      return;
    }

    const loadReferences = async () => {
      setLoadingReferences(true);
      setError("");

      const params = companyId ? { company_id: companyId } : {};

      try {
        // Loading form references must be read-only. Inventory setup/repair is
        // intentionally available only from the explicit Repair Setup button.
        const [typeResponse, parentResponse] = await Promise.all([
          coaApi.getAccountingTypes(params),
          coaApi.getAccountDropdown({
            ...params,
            includeInactive: false,
            include_inactive: false,
          }),
        ]);

        setAccountingTypes(unwrapArray(typeResponse));
        setParentAccounts(unwrapArray(parentResponse));
      } catch (requestError) {
        setAccountingTypes([]);
        setParentAccounts([]);
        setError(requestError.message || "Failed to load account setup");
      } finally {
        setLoadingReferences(false);
      }
    };

    loadReferences();
  }, [open, form.company_id, isSystemAdmin]);

  useEffect(() => {
    if (!account || !accountingTypes.length) return;

    const selected = accountingTypes.find(
      (item) => String(item._id) === String(form.accounting_type),
    );

    const selectedMainType = canonicalMainType(selected?.type);

    if (selected && form.main_type !== selectedMainType) {
      setForm((current) => ({ ...current, main_type: selectedMainType }));
    }
  }, [account, accountingTypes, form.accounting_type, form.main_type]);

  const mainTypes = useMemo(() => {
    const available = new Set(
      accountingTypes.map((item) => canonicalMainType(item.type)),
    );
    return MAIN_TYPE_ORDER.filter((item) => available.has(item));
  }, [accountingTypes]);

  const subtypeOptions = useMemo(
    () =>
      accountingTypes.filter(
        (item) =>
          !form.main_type || canonicalMainType(item.type) === form.main_type,
      ),
    [accountingTypes, form.main_type],
  );

  const selectedType = useMemo(
    () =>
      accountingTypes.find(
        (item) => String(item._id) === String(form.accounting_type),
      ),
    [accountingTypes, form.accounting_type],
  );

  const eligibleParents = useMemo(
    () =>
      parentAccounts
        .filter((item) => {
          if (account && String(item._id) === String(account._id)) return false;

          const itemTypeId =
            getId(item.accounting_type) || getId(item.accountingType);

          return (
            form.accounting_type &&
            String(itemTypeId) === String(form.accounting_type) &&
            item.status === "active"
          );
        })
        .sort((first, second) =>
          String(first.account_code || "").localeCompare(
            String(second.account_code || ""),
            undefined,
            { numeric: true },
          ),
        ),
    [parentAccounts, form.accounting_type, account],
  );

  const setField = (name, value) => {
    setForm((current) => {
      const next = { ...current, [name]: value };

      if (name === "company_id") {
        next.main_type = "";
        next.accounting_type = "";
        next.parent_account = "";
        next.account_code = "";
        // ⭐ When a system admin picks a different company in create mode,
        // reset the currency to the current default. They can override it
        // manually if needed.
        if (!account) {
          next.currency_code = resolvedDefaultCurrency;
        }
      }

      if (name === "main_type") {
        next.accounting_type = "";
        next.parent_account = "";
        next.account_code = "";
      }

      if (name === "accounting_type") {
        next.parent_account = "";
        next.account_code = "";
      }

      if (name === "parent_account") {
        next.account_code = "";
      }

      return next;
    });
  };

  const selectParent = async (parentAccountId) => {
    setField("parent_account", parentAccountId);

    if (!parentAccountId || account) return;

    setLoadingCode(true);
    setError("");

    try {
      const response = await coaApi.getNextCode({
        company_id: form.company_id,
        parent_account: parentAccountId,
        accounting_type: form.accounting_type,
      });

      const generatedCode =
        response?.data?.account_code || response?.account_code || "";

      setForm((current) => ({
        ...current,
        parent_account: parentAccountId,
        account_code: generatedCode,
      }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoadingCode(false);
    }
  };

  const validateForm = () => {
    if (!form.company_id) return "Company is required";
    if (!form.main_type) return "Main type is required";
    if (!form.accounting_type) return "Sub type is required";
    if (!form.parent_account) return "Parent account is required";
    if (!form.account_code.trim()) return "Account code could not be generated";
    if (!form.account_name.trim()) return "Account name is required";

    if (!/^[A-Za-z]{3}$/.test(form.currency_code || "")) {
      return "Currency code must contain 3 letters";
    }

    if (
      form.opening_side !== "none" &&
      Number(form.opening_amount || 0) < 0
    ) {
      return "Opening amount cannot be negative";
    }

    return "";
  };

  const submit = async (event) => {
    event.preventDefault();

    const validationMessage = validateForm();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setSaving(true);
    setError("");

    const openingAmount = Math.max(Number(form.opening_amount || 0), 0);

    const payload = {
      account_code: form.account_code,
      account_name: form.account_name.trim(),
      accounting_type: form.accounting_type,
      parent_account: form.parent_account,
      company_id: form.company_id,
      description: form.description.trim(),
      // ⭐ Always send a clean, uppercase 3-letter code
      currency_code: normalizeCurrencyCode(
        form.currency_code,
        resolvedDefaultCurrency,
      ),
      is_control_account: false,
      allow_manual_posting: true,
      opening_debit:
        form.opening_side === "debit" ? openingAmount : 0,
      opening_credit:
        form.opening_side === "credit" ? openingAmount : 0,
      type: selectedType?.type,
      paymentstype: null,
    };

    try {
      if (account?._id) {
        await coaApi.update(account._id, payload);
      } else {
        await coaApi.create(payload);
      }

      await Promise.resolve(onSaved?.());
      onClose?.();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="coa-modal-backdrop" role="presentation">
      <div
        className="coa-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="coa-form-title"
      >
        <div className="coa-modal__header">
          <div>
            <p className="coa-eyebrow">Chart of Accounts</p>
            <h2 id="coa-form-title">
              {account ? "Edit subaccount" : "Create subaccount"}
            </h2>
          </div>

          <button
            className="coa-icon-button"
            type="button"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
          >
            ×
          </button>
        </div>

        <form onSubmit={submit}>
          <div className="coa-modal__body">
            {error && <div className="coa-alert coa-alert--error">{error}</div>}

            <div className="coa-alert">
              The 19 standard inventory accounts are protected. This form creates
              only a subaccount, and its code is generated automatically from the
              selected parent account.
            </div>
            <div className="coa-form-grid">
              {isSystemAdmin && (
                <label className="coa-field coa-field--full">
                  <span>Company *</span>
                  <select
                    value={form.company_id}
                    onChange={(event) => setField("company_id", event.target.value)}
                    required
                    disabled={loadingCompanies || Boolean(account)}
                  >
                    <option value="">
                      {loadingCompanies ? "Loading companies…" : "Select company"}
                    </option>
                    {companies.map((companyItem) => (
                      <option key={companyItem._id} value={companyItem._id}>
                        {companyItem.company_name || companyItem.name}
                        {companyItem.company_code
                          ? ` (${companyItem.company_code})`
                          : ""}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="coa-field">
                <span>Main type *</span>
                <select
                  value={form.main_type}
                  onChange={(event) => setField("main_type", event.target.value)}
                  required
                  disabled={loadingReferences || Boolean(account)}
                >
                  <option value="">
                    {loadingReferences ? "Loading types…" : "Select main type"}
                  </option>
                  {mainTypes.map((type) => (
                    <option key={type} value={type}>
                      {MAIN_TYPE_LABELS[type] || type}
                    </option>
                  ))}
                </select>
              </label>

              <label className="coa-field">
                <span>Sub type *</span>
                <select
                  value={form.accounting_type}
                  onChange={(event) =>
                    setField("accounting_type", event.target.value)
                  }
                  required
                  disabled={
                    loadingReferences || !form.main_type || Boolean(account)
                  }
                >
                  <option value="">Select sub type</option>
                  {subtypeOptions.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.sub_type} — {type.normal_balance}
                    </option>
                  ))}
                </select>
              </label>

              <label className="coa-field coa-field--full">
                <span>Parent account *</span>
                <select
                  value={form.parent_account}
                  onChange={(event) => selectParent(event.target.value)}
                  required
                  disabled={
                    loadingReferences ||
                    !form.accounting_type ||
                    Boolean(account)
                  }
                >
                  <option value="">Select parent account</option>
                  {eligibleParents.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.account_code} — {item.account_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="coa-field">
                <span>Account code *</span>
                <input
                  value={loadingCode ? "Generating…" : form.account_code}
                  readOnly
                  placeholder="Generated after parent selection"
                  required
                />
              </label>

              <label className="coa-field">
                <span>Account name *</span>
                <input
                  value={form.account_name}
                  onChange={(event) => setField("account_name", event.target.value)}
                  placeholder="Example: Main Bank - USD"
                  maxLength={150}
                  required
                />
              </label>

              <label className="coa-field">
                <span>Currency code</span>
                <input
                  value={form.currency_code}
                  onChange={(event) =>
                    setField("currency_code", event.target.value.toUpperCase())
                  }
                  placeholder={resolvedDefaultCurrency}
                  maxLength={3}
                  minLength={3}
                  required
                />
              </label>

              {!account && (
                <>
                  <label className="coa-field">
                    <span>Opening balance side</span>
                    <select
                      value={form.opening_side}
                      onChange={(event) =>
                        setField("opening_side", event.target.value)
                      }
                    >
                      <option value="none">No opening balance</option>
                      <option value="debit">Debit</option>
                      <option value="credit">Credit</option>
                    </select>
                  </label>

                  <label className="coa-field coa-field--full">
                    <span>Opening amount</span>
                    <input
                      type="number"
                      value={form.opening_amount}
                      onChange={(event) =>
                        setField("opening_amount", event.target.value)
                      }
                      min="0"
                      step="0.01"
                      placeholder="Enter opening balance amount"
                      disabled={form.opening_side === "none"}
                      required={form.opening_side !== "none"}
                    />
                  </label>
                </>
              )}

              <label className="coa-field coa-field--full">
                <span>Description</span>
                <textarea
                  value={form.description}
                  onChange={(event) => setField("description", event.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Purpose and usage of this subaccount"
                />
              </label>
            </div>
          </div>

          <div className="coa-modal__footer">
            <button
              type="button"
              className="coa-button coa-button--secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="coa-button coa-button--primary"
              disabled={saving || loadingReferences || loadingCode}
            >
              {saving
                ? "Saving…"
                : account
                  ? "Save changes"
                  : "Create subaccount"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}